import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material"
import { badgeColor, fmtNum } from "../utils/analyzeStats"
import type { ChatAnalyze } from "../interfaces"

interface SummaryTableProps {
  stats: ChatAnalyze.Stats
}

type Row = {
  label: string
  value: string
  hint: string
  color?: ChatAnalyze.BadgeColor
}

const chipColorMap: Record<
  NonNullable<ChatAnalyze.BadgeColor>,
  "default" | "success" | "warning" | "error"
> = {
  default: "default",
  success: "success",
  warning: "warning",
  error: "error",
}

export function SummaryTable({ stats }: SummaryTableProps) {
  const msgsPerDay = stats.totalDays ? stats.total / stats.totalDays : 0

  const rows: Row[] = [
    {
      label: "Период данных",
      value: `${stats.totalDays} дней`,
      hint: "чем больше — тем надёжнее статистика",
    },
    {
      label: "Уник. авторов (люди)",
      value: fmtNum(stats.allUsersCount - stats.botUsersCount),
      hint: "реальный охват чата",
    },
    {
      label: "DAU среднее",
      value: fmtNum(stats.avgDau, 1),
      hint: "активных авторов в день, норма: ≥10",
      color: badgeColor(stats.avgDau, 10, 5, 0, false),
    },
    {
      label: "WAU среднее",
      value: fmtNum(stats.avgWau, 1),
      hint: "активных авторов в неделю, норма: ≥20",
      color: badgeColor(stats.avgWau, 20, 10, 0, false),
    },
    {
      label: "MAU среднее",
      value: fmtNum(stats.avgMau, 1),
      hint: "активных авторов в месяц",
    },
    {
      label: "Stickiness DAU/MAU",
      value: `${fmtNum(stats.stickiness, 1)}%`,
      hint: "TG-норма 10–35%, <5% — плохо",
      color: badgeColor(stats.stickiness, 15, 8, 0, false),
    },
    {
      label: "Сообщений/день",
      value: fmtNum(msgsPerDay, 0),
      hint: "норма: ≥50, <20 — чат почти мёртвый",
      color: badgeColor(msgsPerDay, 50, 20, 0, false),
    },
    {
      label: "Реакции/сообщение",
      value: stats.reactionRate.toFixed(3),
      hint: "городской чат: 0.03–0.15 — норма",
      color: badgeColor(stats.reactionRate, 0.03, 0.01, 0, false),
    },
    {
      label: "Ответы (reply)",
      value: `${fmtNum(stats.replyRate, 1)}%`,
      hint: ">30% — реальные диалоги",
      color: badgeColor(stats.replyRate, 20, 10, 0, false),
    },
    {
      label: "Доля ботов",
      value: `${fmtNum(stats.botShare * 100, 1)}%`,
      hint: "норма: <10%",
      color: badgeColor(stats.botShare * 100, 10, 20, 40),
    },
    {
      label: "Ночная активность",
      value: `${fmtNum(stats.nightShare, 1)}%`,
      hint: "норма: <15%, >25% — возможны боты",
      color: badgeColor(stats.nightShare, 15, 20, 25),
    },
    {
      label: "Дубликаты сообщений",
      value: `${fmtNum(stats.duplicateRatio, 1)}%`,
      hint: "норма: <10%, >25% — подозрительно",
      color: badgeColor(stats.duplicateRatio, 10, 20, 30),
    },
    {
      label: "Fraud Score",
      value: `${stats.fraudScore}/100`,
      hint: "0–20: ок, 20–40: проверить, 40+: риск",
      color: badgeColor(stats.fraudScore, 20, 40, 65),
    },
    {
      label: "Итог",
      value: stats.verdict.label,
      hint: stats.verdict.recommendation,
      color:
        stats.verdict.type === "success"
          ? "success"
          : stats.verdict.type === "warning"
            ? "warning"
            : "error",
    },
  ]

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, width: "30%" }}>
              Показатель
            </TableCell>
            <TableCell sx={{ fontWeight: 700, width: "20%" }}>
              Значение
            </TableCell>
            <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>
              Ориентир / Норма
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.label} sx={{ "&:last-child td": { border: 0 } }}>
              <TableCell>
                <strong>{row.label}</strong>
              </TableCell>
              <TableCell>
                <Chip
                  label={row.value}
                  size="small"
                  color={row.color ? chipColorMap[row.color] : "default"}
                  sx={{ fontWeight: 600 }}
                />
              </TableCell>
              <TableCell sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                {row.hint}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
